// Video session client (PRD-0010 §1).
//
// Thin wrapper over the three session routes. Deliberately has no React in it,
// so the lifecycle can be reasoned about — and tested — without a renderer.
//
// The only credential that ever reaches this file is a SUBSCRIBE-only
// participant token. The publisher token exists solely on the robot's side of
// the wire, delivered inside an MQTT command; there is no code path in the
// browser that could obtain one.

import { apiFetch } from '../auth/apiFetch.ts';

export interface VideoSession {
  session_id: string;
  /** `starting` when we opened it; `joined` when someone was already watching. */
  state: 'starting' | 'joined';
  stage_arn: string;
  token: string;
  expires_in_s: number;
  robot_id: string;
  camera_topic: string;
  /** What is ACTUALLY being recorded — not what was asked for. A flagged
   *  session whose composition failed to start comes back false, with the
   *  reason below, rather than claiming a recording that does not exist. */
  recording: boolean;
  recording_error?: string | null;
  viewers: number;
}

export interface Heartbeat {
  session_id: string;
  state: 'live';
  viewers: number;
  /** Present only when the server decided to renew (D18). */
  token?: string;
  expires_in_s?: number;
}

/** The session has ended server-side; stop beating rather than retrying. */
export class SessionGone extends Error {}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 410) throw new SessionGone('session has ended');
  if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
  return res.json() as Promise<T>;
}

export async function openSession(
  robotId: string,
  opts: { cameraTopic?: string; record?: boolean } = {},
): Promise<VideoSession> {
  const q = new URLSearchParams();
  if (opts.cameraTopic) q.set('camera_topic', opts.cameraTopic);
  if (opts.record) q.set('record', 'true');
  const qs = q.toString();
  return json<VideoSession>(await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/video/session${qs ? `?${qs}` : ''}`,
    { method: 'POST' },
  ));
}

export async function heartbeat(
  robotId: string, sessionId: string, expiresInS: number,
): Promise<Heartbeat> {
  // expires_in_s is what lets the server renew ONLY when needed — minting on
  // every beat would burn IVS calls for nothing.
  return json<Heartbeat>(await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/video/session/`
    + `${encodeURIComponent(sessionId)}/heartbeat?expires_in_s=${Math.max(0, Math.floor(expiresInS))}`,
    { method: 'POST' },
  ));
}

export async function leaveSession(
  robotId: string, sessionId: string, opts: { keepalive?: boolean } = {},
): Promise<void> {
  // `keepalive` so the request survives the page being torn down. Without it a
  // closing tab silently skips the leave and the session hangs around until the
  // reaper notices — correct, but a minute later than it needed to be.
  const init: RequestInit = { method: 'DELETE' };
  // Only set when asked: exactOptionalPropertyTypes makes `keepalive:
  // undefined` a different thing from an absent key.
  if (opts.keepalive) init.keepalive = true;
  await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/video/session/${encodeURIComponent(sessionId)}`,
    init,
  ).catch(() => undefined);
}
